// src/utils/firestoreUtils.js
import { collection, addDoc, getDocs, doc, updateDoc, getDoc, query, where, deleteDoc, setDoc, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from './firebase';

// Add a vehicle to the user's collection
export const addVehicle = async (userId, data) => {
  await addDoc(collection(db, `users/${userId}/vehicles`), data);
};

// Add a lap to the user's collection
export const addLap = async (userId, data) => {
  await addDoc(collection(db, `users/${userId}/laps`), data);
};

// Update a lap
export const updateLap = async (userId, lapId, data) => {
  const lapRef = doc(db, `users/${userId}/laps/${lapId}`);
  await updateDoc(lapRef, data);
};

// Delete a lap
export const deleteLap = async (userId, lapId) => {
  const lapRef = doc(db, `users/${userId}/laps/${lapId}`);
  await deleteDoc(lapRef);
};

// Get all circuits
export const getCircuits = async () => {
  const snapshot = await getDocs(collection(db, 'circuits'));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// Get a single circuit by id
export const getCircuit = async (id) => {
  const circuitRef = doc(db, 'circuits', id);
  const circuitSnap = await getDoc(circuitRef);
  if (circuitSnap.exists()) {
    return { id: circuitSnap.id, ...circuitSnap.data() };
  }
  return null;
};

// Add a new circuit
export const addCircuit = async (data) => {
  const ref = await addDoc(collection(db, 'circuits'), data);
  return ref.id;
};

// Update a circuit
export const updateCircuit = async (id, data) => {
  const circuitRef = doc(db, 'circuits', id);
  await updateDoc(circuitRef, data);
};

// Delete a circuit
export const deleteCircuit = async (id) => {
  const circuitRef = doc(db, 'circuits', id);
  await deleteDoc(circuitRef);
};

// Check if circuit is used in any laps
export const isCircuitUsed = async (circuitId) => {
  const usersSnapshot = await getDocs(collection(db, 'users'));
  for (const userDoc of usersSnapshot.docs) {
    const lapsSnapshot = await getDocs(query(collection(db, `users/${userDoc.id}/laps`), where('track', '==', circuitId)));
    if (!lapsSnapshot.empty) {
      return true;
    }
  }
  return false;
};

// Get pending laps (hasBeenReviewed: false) - For admins only
export const getPendingLaps = async () => {
  const usersSnapshot = await getDocs(collection(db, 'users'));
  let pending = [];
  for (const userDoc of usersSnapshot.docs) {
    const lapsSnapshot = await getDocs(query(collection(db, `users/${userDoc.id}/laps`), where('hasBeenReviewed', '==', false)));
    lapsSnapshot.docs.forEach((lapDoc) => {
      pending.push({ id: lapDoc.id, userId: userDoc.id, userName: userDoc.data().displayName || 'Unknown', ...lapDoc.data() });
    });
  }
  return pending;
};

// Get all laps for admin review - sorted by date
export const getAllLaps = async () => {
  const usersSnapshot = await getDocs(collection(db, 'users'));
  let allLaps = [];
  for (const userDoc of usersSnapshot.docs) {
    const lapsSnapshot = await getDocs(collection(db, `users/${userDoc.id}/laps`));
    lapsSnapshot.docs.forEach((lapDoc) => {
      allLaps.push({ id: lapDoc.id, userId: userDoc.id, userName: userDoc.data().displayName || 'Unknown', ...lapDoc.data() });
    });
  }
  // Sort by date descending (newest first)
  return allLaps.sort((a, b) => new Date(b.date) - new Date(a.date));
};

// Approve a lap and update points
export const approveLap = async (lapId, userId) => {
  const lapRef = doc(db, `users/${userId}/laps/${lapId}`);
  const lapSnap = await getDoc(lapRef);
  if (!lapSnap.exists()) return;
  const lapData = lapSnap.data();
  console.log('Approving lap:', lapId, 'for user:', userId, 'track:', lapData.track);
  await updateDoc(lapRef, { hasBeenReviewed: true, isApproved: true });
  // Recalculate points for the circuit
  await recalculatePointsForCircuit(lapData.track, new Date().getFullYear());
};

// Deny a lap
export const denyLap = async (lapId, userId) => {
  const lapRef = doc(db, `users/${userId}/laps/${lapId}`);
  const lapSnap = await getDoc(lapRef);
  if (!lapSnap.exists()) return;
  const lapData = lapSnap.data();
  await updateDoc(lapRef, { hasBeenReviewed: true, isApproved: false });
  // Recalculate points for the circuit
  await recalculatePointsForCircuit(lapData.track, new Date().getFullYear());
};

// Get leaderboard - Aggregate points from approved laps
export const getLeaderboard = async () => {
  const CACHE_KEY = 'lb_cache';
  const CACHE_TTL = 90000;
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const { ts, data } = JSON.parse(cached);
      if (Date.now() - ts < CACHE_TTL) return data;
    }
  } catch (_) {}

  const currentYear = new Date().getFullYear();
  const usersSnapshot = await getDocs(collection(db, 'users'));

  const leaderboard = await Promise.all(usersSnapshot.docs.map(async (userDoc) => {
    const lapsSnapshot = await getDocs(query(collection(db, `users/${userDoc.id}/laps`), where('isApproved', '==', true)));
    let points = 0;
    const headshot = userDoc.data().headshot || null;
    lapsSnapshot.docs.forEach((lap) => {
      const lapData = lap.data();
      if (lapData.date && new Date(lapData.date).getFullYear() === currentYear) {
        points += lapData.points || 0;
      }
    });
    return { userId: userDoc.id, name: userDoc.data().displayName || 'Unknown', points, headshot };
  }));

  const sorted = leaderboard.sort((a, b) => b.points - a.points);
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data: sorted })); } catch (_) {}
  return sorted;
};

// Get user role
export const getUserRole = async (uid) => {
  const userDoc = await getDoc(doc(db, 'users', uid));
  return userDoc.exists() ? userDoc.data().role || 'user' : 'user';
};

// Get user vehicles
export const getUserVehicles = async (userId) => {
  const snapshot = await getDocs(collection(db, `users/${userId}/vehicles`));
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// Update a vehicle
export const updateVehicle = async (userId, vehicleId, data) => {
  const vehicleRef = doc(db, `users/${userId}/vehicles/${vehicleId}`);
  await updateDoc(vehicleRef, data);
};

// Delete a vehicle (only if no laps associated)
export const deleteVehicle = async (userId, vehicleId) => {
  // Check if there are laps associated with this vehicle
  const lapsSnapshot = await getDocs(query(collection(db, `users/${userId}/laps`), where('vehicleId', '==', vehicleId)));
  if (!lapsSnapshot.empty) {
    throw new Error('Cannot delete vehicle with associated laps. Delete laps first.');
  }
  const vehicleRef = doc(db, `users/${userId}/vehicles/${vehicleId}`);
  await deleteDoc(vehicleRef);
};

// Get user laps - For public/profile, only approved; for own/admin, all (but here we default to approved for safety)
export const getUserLaps = async (userId, onlyApproved = true) => {
  let lapsQuery = collection(db, `users/${userId}/laps`);
  if (onlyApproved) {
    lapsQuery = query(lapsQuery, where('isApproved', '==', true));
  }
  const snapshot = await getDocs(lapsQuery);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// Update user profile
export const updateUserProfile = async (userId, updates) => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, updates);
};

// Get user profile
export const getUserProfile = async (userId) => {
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) return null;
  const data = userDoc.data();
  const vehicles = await getUserVehicles(userId);
  return {
    name: data.displayName,
    bio: data.bio || '',
    headshot: data.headshot || '',
    vehicles: vehicles,
    totalPoints: data.totalPoints || 0, // Current season points only
  };
};

// Get user laps by circuit - For profile, only approved laps
export const getUserLapsByCircuit = async (userId) => {
  const laps = await getUserLaps(userId, true); // Only approved
  const circuitPromises = laps.map(async (lap) => {
    const [circuitId, layoutName] = lap.track.split('-');
    const circuit = await getCircuit(circuitId);
    if (circuit) {
      const layout = circuit.layouts.find(l => l.layoutName === layoutName);
      return {
        ...lap,
        trackName: `${circuit.name} - ${layoutName}`,
        trackMap: layout ? layout.map : '',
        points: lap.points || 0,
      };
    }
    return {
      ...lap,
      trackName: lap.track,
      trackMap: '',
      points: lap.points || 0,
    };
  });
  return await Promise.all(circuitPromises);
};

// Recalculate points for all users on a specific circuit
export const recalculatePointsForCircuit = async (circuitId, year) => {
  console.log('Recalculating points for circuit:', circuitId, 'year:', year);
  // Get points structure
  const pointsData = await getPointsStructure(year);
  if (!pointsData) {
    console.log('No points data found for year:', year);
    return;
  }
  const pointsTable = pointsData.pointsTable;
  console.log('Points table:', pointsTable);

  // Get all approved laps for this circuit and year
  const usersSnapshot = await getDocs(collection(db, 'users'));
  let allLaps = [];
  for (const userDoc of usersSnapshot.docs) {
    const lapsSnapshot = await getDocs(query(collection(db, `users/${userDoc.id}/laps`), where('isApproved', '==', true), where('track', '==', circuitId)));
    lapsSnapshot.docs.forEach(doc => {
      const lapData = doc.data();
      // Only include laps from the specified year
      const lapDate = lapData.date?.toDate ? lapData.date.toDate() : new Date(lapData.date);
      if (lapDate.getFullYear() === year) {
        allLaps.push({ id: doc.id, userId: userDoc.id, ...lapData });
      }
    });
  }
  console.log('All approved laps for circuit:', allLaps);

  // Group by user and take the best (fastest) lap per user
  const bestLapsByUser = {};
  allLaps.forEach(lap => {
    if (!bestLapsByUser[lap.userId] || lap.bestLap < bestLapsByUser[lap.userId].bestLap) {
      bestLapsByUser[lap.userId] = lap;
    }
  });
  const bestLaps = Object.values(bestLapsByUser);
  console.log('Best laps per user:', bestLaps);

  // Sort by bestLap ascending (lower time is better)
  bestLaps.sort((a, b) => a.bestLap - b.bestLap);
  console.log('Sorted best laps:', bestLaps.map(l => ({ userId: l.userId, time: l.bestLap })));

  // Assign points to best laps
  const numCompetitors = bestLaps.length;
  console.log('Number of competitors (best laps):', numCompetitors);
  for (let i = 0; i < bestLaps.length; i++) {
    const position = i + 1;
    const points = pointsTable[position - 1][numCompetitors - 1] || 0;
    console.log(`Position ${position}, competitors ${numCompetitors}, points ${points}`);
    // Update lap points
    await updateDoc(doc(db, `users/${bestLaps[i].userId}/laps/${bestLaps[i].id}`), { points });
    console.log('Updated best lap', bestLaps[i].id, 'with points', points);
  }

  // Set points to 0 for non-best laps
  const bestLapIds = new Set(bestLaps.map(l => l.id));
  for (const lap of allLaps) {
    if (!bestLapIds.has(lap.id)) {
      await updateDoc(doc(db, `users/${lap.userId}/laps/${lap.id}`), { points: 0 });
      console.log('Updated non-best lap', lap.id, 'with points 0');
    }
  }

  // Recalculate totalPoints for affected users (current year only)
  const affectedUsers = [...new Set(bestLaps.map(l => l.userId))];
  console.log('Affected users:', affectedUsers);
  for (const userId of affectedUsers) {
    const userLaps = await getUserLaps(userId, true);
    // Filter to current year only, matching leaderboard logic
    const currentYearLaps = userLaps.filter(lap => {
      const lapDate = lap.date?.toDate ? lap.date.toDate() : new Date(lap.date);
      return lapDate.getFullYear() === year;
    });
    const totalPoints = currentYearLaps.reduce((sum, lap) => sum + (lap.points || 0), 0);
    console.log('User', userId, 'current year total points:', totalPoints);
    await updateDoc(doc(db, 'users', userId), { totalPoints });
  }
};

// Get points structure for a year
export const getPointsStructure = async (year) => {
  const docRef = doc(db, 'seriesAdmin', year.toString());
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    // Convert object back to 2D array
    const pointsTable = Array(15).fill().map(() => Array(15).fill(0));
    if (data.pointsTable) {
      Object.keys(data.pointsTable).forEach(key => {
        const [pos, comp] = key.split('-').map(Number);
        pointsTable[pos - 1][comp - 1] = data.pointsTable[key];
      });
    }
    return { ...data, pointsTable };
  }
  return null;
};

// Save points structure for a year
export const savePointsStructure = async (year, pointsTable, isLocked = true) => {
  const docRef = doc(db, 'seriesAdmin', year.toString());
  // Convert 2D array to object for Firestore compatibility
  const pointsObj = {};
  pointsTable.forEach((row, pos) => {
    row.forEach((points, comp) => {
      pointsObj[`${pos + 1}-${comp + 1}`] = points;
    });
  });
  await setDoc(docRef, { pointsTable: pointsObj, isLocked });
};

// Add a post to the community
export const addPost = async (data) => {
  await addDoc(collection(db, 'posts'), data);
};

// Get all posts (for notifications) - sorted by timestamp, newest first
export const getAllPosts = async () => {
  const q = query(collection(db, 'posts'), orderBy('timestamp', 'desc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// Get initial posts (20 newest posts) - for community feed
export const getPosts = async () => {
  const q = query(collection(db, 'posts'), orderBy('timestamp', 'desc'), limit(20));
  const snapshot = await getDocs(q);
  const posts = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Fetch user headshots for each post
  const postsWithHeadshots = await Promise.all(
    posts.map(async (post) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', post.userId));
        const userData = userDoc.data();
        return {
          ...post,
          userHeadshot: userData?.headshot || null,
        };
      } catch (error) {
        console.error('Error fetching user headshot for post:', post.id, error);
        return post;
      }
    })
  );

  return postsWithHeadshots;
};

// Get more posts (pagination)
export const getMorePosts = async (lastPost) => {
  if (!lastPost || !lastPost.timestamp) return [];
  const q = query(
    collection(db, 'posts'),
    orderBy('timestamp', 'desc'),
    startAfter(lastPost.timestamp),
    limit(20)
  );
  const snapshot = await getDocs(q);
  const posts = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

  // Fetch user headshots for each post
  const postsWithHeadshots = await Promise.all(
    posts.map(async (post) => {
      try {
        const userDoc = await getDoc(doc(db, 'users', post.userId));
        const userData = userDoc.data();
        return {
          ...post,
          userHeadshot: userData?.headshot || null,
        };
      } catch (error) {
        console.error('Error fetching user headshot for post:', post.id, error);
        return post;
      }
    })
  );

  return postsWithHeadshots;
};

// Like or unlike a post
export const likePost = async (postId, userId) => {
  const postRef = doc(db, 'posts', postId);
  const postSnap = await getDoc(postRef);
  if (postSnap.exists()) {
    const post = postSnap.data();
    const likes = post.likes || [];
    const updatedLikes = likes.includes(userId) ? likes.filter(id => id !== userId) : [...likes, userId];
    await updateDoc(postRef, { likes: updatedLikes });
  }
};

// Add a comment to a post
export const addComment = async (postId, userId, userName, text) => {
  const postRef = doc(db, 'posts', postId);
  const postSnap = await getDoc(postRef);
  if (postSnap.exists()) {
    const post = postSnap.data();
    const comments = post.comments || [];
    const newComment = { userId, userName, text, timestamp: new Date() };
    await updateDoc(postRef, { comments: [...comments, newComment] });
  }
};

// Delete a post
export const deletePost = async (postId) => {
  const postRef = doc(db, 'posts', postId);
  await deleteDoc(postRef);
};

// Delete a comment from a post
export const deleteComment = async (postId, commentIndex) => {
  const postRef = doc(db, 'posts', postId);
  const postSnap = await getDoc(postRef);
  if (postSnap.exists()) {
    const post = postSnap.data();
    const comments = post.comments || [];
    comments.splice(commentIndex, 1);
    await updateDoc(postRef, { comments });
  }
};

// Edit a post
export const editPost = async (postId, updates) => {
  const postRef = doc(db, 'posts', postId);
  await updateDoc(postRef, updates);
};

// Update user's last community visit timestamp
export const updateUserLastCommunityVisit = async (userId) => {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    lastCommunityVisit: new Date()
  });
};

// Get user's last community visit timestamp
export const getUserLastCommunityVisit = async (userId) => {
  const userRef = doc(db, 'users', userId);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    return userSnap.data().lastCommunityVisit;
  }
  return null;
};

// Vehicle Models Management
export const getVehicleModels = async () => {
  const modelsRef = collection(db, 'vehicleModels');
  const snapshot = await getDocs(modelsRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const addVehicleModel = async (modelName) => {
  await addDoc(collection(db, 'vehicleModels'), { name: modelName });
};

export const deleteVehicleModel = async (modelId) => {
  await deleteDoc(doc(db, 'vehicleModels', modelId));
};

// ─── Calendar Events ────────────────────────────────────────────
export const getEvents = async () => {
  const snapshot = await getDocs(collection(db, 'events'));
  const events = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  // Sort client-side so no Firestore index is required
  return events.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
};

export const addEvent = async (data) => {
  const ref = await addDoc(collection(db, 'events'), data);
  return ref.id;
};

export const updateEvent = async (eventId, data) => {
  await updateDoc(doc(db, 'events', eventId), data);
};

export const deleteEvent = async (eventId) => {
  await deleteDoc(doc(db, 'events', eventId));
};

export const joinEvent = async (eventId, userId) => {
  const ref = doc(db, 'events', eventId);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    const attendees = snap.data().attendees || [];
    const updated = attendees.includes(userId)
      ? attendees.filter(id => id !== userId)
      : [...attendees, userId];
    await updateDoc(ref, { attendees: updated });
    return !attendees.includes(userId); // true = joined
  }
};

// ─── News ────────────────────────────────────────────────────
export const getNewsItems = async () => {
  const snapshot = await getDocs(collection(db, 'news'));
  const items = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  // Sort by user-entered date descending
  return items.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
};

export const addNewsItem = async (data) => {
  const ref = await addDoc(collection(db, 'news'), {
    ...data,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  return ref.id;
};

export const updateNewsItem = async (id, data) => {
  await updateDoc(doc(db, 'news', id), {
    ...data,
    updatedAt: new Date().toISOString(),
  });
};

export const deleteNewsItem = async (id) => {
  await deleteDoc(doc(db, 'news', id));
};

export const updateUserLastNewsVisit = async (userId) => {
  await updateDoc(doc(db, 'users', userId), { lastNewsVisit: new Date() });
};

export const getUserLastNewsVisit = async (userId) => {
  const snap = await getDoc(doc(db, 'users', userId));
  return snap.exists() ? snap.data().lastNewsVisit : null;
};

